// StudentPaymentRow — one <tr> in the student detail page's payments table.
// "Mark paid" reuses the same action logic as the dashboard's recent-payments
// row (useMarkPaymentPaid), different (table) layout.
import { useState } from "react";
import { Link } from "react-router-dom";
import MarkPaidControl from "./MarkPaidControl.js";
import ProofSummary from "./ProofSummary.js";
import Badge from "../shared/Badge.js";
import { formatKes } from "../../lib/formatMoney.js";
import { formatDate } from "../../lib/formatDateTime.js";
import type { PaymentWithProof, TransactionStatus } from "@dm-lms/shared/types";

const STATUS_TONE: Record<TransactionStatus, "green" | "gold" | "red" | "gray"> = {
  success:  "green",
  pending:  "gold",
  failed:   "red",
  refunded: "gray",
};

const STATUS_LABEL: Record<TransactionStatus, string> = {
  success:  "Paid",
  pending:  "Pending",
  failed:   "Failed",
  refunded: "Refunded",
};

interface StudentPaymentRowProps {
  payment: PaymentWithProof;
}

export default function StudentPaymentRow({ payment: initial }: StudentPaymentRowProps) {
  const [payment, setPayment] = useState(initial);
  return (
    <tr>
      <td className="px-4 py-3 font-medium text-dm-navy">{payment.code}</td>
      <td className="px-4 py-3">{formatKes(payment.amountKes)}</td>
      <td className="px-4 py-3 capitalize">{payment.method}</td>
      <td className="px-4 py-3">
        <Badge tone={STATUS_TONE[payment.status]}>{STATUS_LABEL[payment.status]}</Badge>
        <ProofSummary payment={payment} />
      </td>
      <td className="px-4 py-3">{payment.paidAt ? formatDate(payment.paidAt) : "—"}</td>
      <td className="px-4 py-3">
        {payment.status === "pending" && payment.reference === null && (
          <MarkPaidControl payment={payment} onPaid={(settled) => setPayment((prev) => ({ ...prev, ...settled }))} />
        )}
        {payment.status === "pending" && payment.reference !== null && (
          <Link to="/app/admin/payments" className="text-xs font-medium text-dm-royal hover:underline">
            Verify on Payments page
          </Link>
        )}
      </td>
    </tr>
  );
}
